import "./completion-tab.scss";
import { P2Alert, P2Button, P2ButtonGroup, P2DateSelector } from "react-bootstrap-common-script";
import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { Separator } from "../../../../separator";
import DocumentationLists from "../../../../../pages/application-coo/documents/documentation-list";
import { FileItem } from "../../../../../models/polymer/Document.model";
import config from "../../../../../config";
import { PolymerAxios } from "../../../../../utils/axios";
import { useAppSelector } from "../../../../../redux/hook";
import { SolicitorCompletionRequest } from "../../../../../models/classes/solicitor/SolicitorCompletionDetail.class";
import { API_Solicitor_CompletionSubmit } from "../../../../../utils/api";
import { SubmitDocuments } from "../../../../../models/classes/CooSubmission.class";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { solicitor_completion_documents } from "../../../../../utils/shared/documents-list";

export function CompletionTab() {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  // States
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);

  const [fileItemsStorage, setFileItemsStorage] = useState<FileItem[]>([]);
  const [actualCompletionDate, setActualCompletionDate] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAlert, setSubmitAlert] = useState<{ variant: "warning" | "error"; text: string } | null>(null);

  const { solicitorCaseSummaryData, solicitorSubmissionsRefetch, solicitorCompletionRefetch } = useSolicitorCaseDetailsContext();

  // Derived values
  const scheduledCompletionDate = solicitorCaseSummaryData?.scheduled_completion_date ?? solicitorCaseSummaryData?.tentative_completion_date;
  // fallback to TCD if SCD is not present

  const isActualCompletionDateValid = useMemo(
    () => actualCompletionDate && scheduledCompletionDate && actualCompletionDate === scheduledCompletionDate,
    [actualCompletionDate, scheduledCompletionDate]
  );

  const isBtnEnabled = useMemo(() => {
    const fileCheckingValid = config.isLocalhost ? true : fileItemsStorage.length > 0 && fileItemsStorage.every((e) => e.key !== "");
    return isActualCompletionDateValid && fileCheckingValid && !isSubmitting;
  }, [actualCompletionDate, fileItemsStorage, isSubmitting]);

  // Functions
  const onCompletionSubmit = useCallback(async () => {
    let documents = fileItemsStorage.map((file) => {
      //NOTE: required_option_checked not track from application-step-4;
      let fileType = "";
      if (file?.file_content?.file_name) {
        const fileNameSplitArray = file?.file_content?.file_name.split(".");
        fileType = fileNameSplitArray[fileNameSplitArray.length - 1];
      }
      return new SubmitDocuments({
        uuid: file.file_id.toString(),
        code: file.code,
        file_name: file.file_content?.file_name,
        label: file.label,
        document_type: fileType,
        required_option_checked: true,
        key: file.key,
        file_size: file.file_content.size,
      });
    });

    let payload = new SolicitorCompletionRequest({
      application_uuid: applicationUuid,
      actual_completion_date: actualCompletionDate,
      submitted_by: loginUser.name,
      documents: documents,
    });

    setIsSubmitting(true);
    setSubmitAlert(null);

    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_CompletionSubmit}`, payload);

      if (response.status === 200) {
        solicitorSubmissionsRefetch();
        solicitorCompletionRefetch();
      }
    } catch (e: unknown) {
      await solicitorCompletionRefetch();

      const status =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined;

      if (status === 422) {
        setSubmitAlert({
          variant: "warning",
          text: "This completion has already been submitted.",
        });
      } else {
        setSubmitAlert({
          variant: "error",
          text: "An error occurred while submitting completion information. Please try again later.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    applicationUuid,
    actualCompletionDate,
    fileItemsStorage,
    loginUser?.uinfin,
    loginUser?.name,
    solicitorCompletionRefetch,
    solicitorSubmissionsRefetch,
    singpassToken,
    uen,
  ]);

  return (
    <div style={{ paddingLeft: 24, paddingRight: 24 }}>
      <h2 className="polymer-completion-tab__title">Completion Details</h2>
      <p>Please update the actual completion date if it differs from the scheduled date above, and submit the following required documents.</p>
      <div style={{ marginBottom: 32 }}>
        <P2DateSelector
          value={actualCompletionDate}
          required
          placeholder="DD/MM/YYYY"
          label="Actual completion date"
          maxInput={moment().format("YYYY-MM-DD")}
          onChange={setActualCompletionDate}
          isError={actualCompletionDate && !isActualCompletionDateValid}
          errorMsg={"Actual completion date must be the same as scheduled completion date"}
        />
      </div>
      <Separator variant="dashed" style={{ marginBottom: 32 }} />
      <div style={{ marginBottom: 32 }}>
        <DocumentationLists
          type={1}
          document_list={solicitor_completion_documents}
          filesItemsStorage={fileItemsStorage}
          setFileItemsStorage={setFileItemsStorage}
        />
      </div>
      <Separator variant="dashed" style={{ marginBottom: 32 }} />
      {submitAlert ? (
        <P2Alert variant={submitAlert.variant} text={submitAlert.text} style={{ marginBottom: 16 }} />
      ) : null}
      <P2ButtonGroup>
        <P2Button onClick={onCompletionSubmit} disabled={!isBtnEnabled} variant="primary-1">
          {isSubmitting ? "Submitting..." : "Submit"}
        </P2Button>
      </P2ButtonGroup>
    </div>
  );
}
